import React, { ReactElement, useState } from 'react';
import {
  Formik, FormikErrors, FormikHelpers, getIn, isString,
} from 'formik';
import {
  Alert, Col, Form, Row,
} from 'react-bootstrap';
import RequiredFieldLabel from '../Partials/RequiredFieldLabel';
import useOnceAfterDelay from '../../../hooks/useOnceAfterDelay';
import { abbreviations } from '../../../common/states';
import { UnsavedSellerContact } from '../../../structures/models/seller';
import { LaravelValidationErrors } from '../../../structures/responses/errors';
import { SellerDetailsValidation } from './validation';
import CreateContactForm from './partials/CreateContactForm';
import { FormValues } from './index';

type SellerDetailsStepProps = {
  onSubmit: (
    values: SellerDetails,
    formikHelpers: FormikHelpers<any>,
    setError: () => void,
    shouldSubmit: boolean,
  ) => void;
  onChange: (values: SellerDetails) => void;
  footer: (isValid: boolean, canGoBack: boolean) => ReactElement;
  hocState: FormValues;
  appendFormMessage: (formMessage: ReactElement) => void;
  submissionErrors: LaravelValidationErrors,
};

export type SellerDetails = {
  name: string;
  address_line_1: string;
  city: string;
  state: string;
  zip: string;
  contacts: UnsavedSellerContact[];
};

const createEmptyContact = (): UnsavedSellerContact => {
  return {
    contact_preference: [],
  };
};

const SellerDetailsStep: React.FC<SellerDetailsStepProps> = ({
  onSubmit,
  onChange,
  footer,
  hocState,
  submissionErrors,
}): ReactElement => {
  const [state, setState] = useState<{ error: string | null }>({
    error: null,
  });

  const initialValues: SellerDetails = {
    name: hocState.seller ? hocState.seller.name : '',
    address_line_1: hocState.seller ? hocState.seller.address_line_1 : '',
    city: hocState.seller ? hocState.seller.city : '',
    state: hocState.seller ? hocState.seller.state : '',
    zip: hocState.seller ? hocState.seller.zip : '',
    contacts: hocState.seller.contacts.length ? hocState.seller.contacts : [createEmptyContact()],
  };

  const setError = (error?: string) => {
    if (error && isString(error)) {
      setState({ ...state, error });
    } else {
      setState({ ...state, error: null });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) => {
        // Validate via JS on submit
        formikHelpers.validateForm(values)
          .then((errors: FormikErrors<any>) => {
            if (Object.keys(errors).length === 0) {
              onSubmit(values, formikHelpers, setError, true);
            }
          });
      }}
      validationSchema={SellerDetailsValidation}
    >
      {(formikProps) => {
        const {
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          validateForm,
          isValid,
        } = formikProps;
        useOnceAfterDelay(100, () => {
          onChange(values);
          // The contact_preference field operates using a chain of async operations;
          // this means that validation doesn't always happen in the correct order.
          // By manually validating the form using a timeout on change, we can avoid
          // issues caused by async code.
          if (touched?.contacts?.length && touched.contacts[0].contact_preference) {
            validateForm();
          }
        }, [values, touched]);

        return (
          <form onSubmit={handleSubmit} className="d-flex flex-column justify-content-between h-100">
            <Row className="p-0 pt-md-3 pl-md-3 pr-md-3 pt-xl-6 pl-xl-6 pr-xl-6">
              <Col xs={12} className="mb-2">
                <h2 className="text-blue-500 mb-0">
                  Please tell us a little about yourself.
                </h2>
              </Col>
              {state.error ? (
                <Col xs={12}>
                  <Alert
                    variant="danger"
                    style={{
                      whiteSpace: 'break-spaces',
                    }}
                  >
                    <span>{state.error}</span>
                    <ul className="mb-0 mt-2">
                      { Object.entries(submissionErrors).map(([key, error]) => (
                        <li className="mb-1" key={key}>
                          {error}
                        </li>
                      )) }
                    </ul>
                  </Alert>
                </Col>
              ) : null}
              <Col xs={12} md={6}>
                <Form.Group controlId="name">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={getIn(touched, 'name') && getIn(errors, 'name')}>
                      Kennel/Breeder Name
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Your kennel/breeder name"
                    value={getIn(values, 'name')}
                    isInvalid={
                      getIn(touched, 'name')
                      && getIn(errors, 'name')
                    }
                    onChange={handleChange('name')}
                    onBlur={handleBlur('name')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getIn(touched, 'name') && getIn(errors, 'name')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="p-0 pl-md-3 pr-md-3 pl-xl-6 pr-xl-6">
              <Col xs={12}>
                <Form.Group controlId="address_line_1">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={(getIn(touched, 'address_line_1') && getIn(errors, 'address_line_1')) || getIn(errors, 'coordinates')}>
                      Address Line 1
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="123 Sesame Street"
                    value={getIn(values, 'address_line_1')}
                    isInvalid={(getIn(touched, 'address_line_1') && getIn(errors, 'address_line_1')) || getIn(errors, 'coordinates')}
                    onChange={handleChange('address_line_1')}
                    onBlur={handleBlur('address_line_1')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'address_line_1')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={12} xl={6}>
                <Form.Group controlId="formCity">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={(getIn(touched, 'city') && getIn(errors, 'city')) || getIn(errors, 'coordinates')}>
                      City
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="City Name"
                    value={getIn(values, 'city')}
                    isInvalid={(getIn(touched, 'city') && getIn(errors, 'city')) || getIn(errors, 'coordinates')}
                    onChange={handleChange('city')}
                    onBlur={handleBlur('city')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'city')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={6} md={3} lg={6} xl={3}>
                <Form.Group controlId="formState">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={(getIn(touched, 'state') && getIn(errors, 'state')) || getIn(errors, 'coordinates')}>
                      State
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    custom
                    value={getIn(values, 'state')}
                    isInvalid={(getIn(touched, 'state') && getIn(errors, 'state')) || getIn(errors, 'coordinates')}
                    onChange={handleChange('state')}
                    onBlur={handleBlur('state')}
                  >
                    <option value="" disabled>-- select --</option>
                    {abbreviations.map((s) => <option key={s}>{s}</option>)}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'state')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={6} md={3} lg={6} xl={3}>
                <Form.Group controlId="formZip">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={(getIn(touched, 'zip') && getIn(errors, 'zip')) || getIn(errors, 'coordinates')}>
                      Zip
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    pattern="[0-9]*"
                    placeholder="12345"
                    value={getIn(values, 'zip')}
                    isInvalid={(getIn(touched, 'zip') && getIn(errors, 'zip')) || getIn(errors, 'coordinates')}
                    onChange={handleChange('zip')}
                    onBlur={handleBlur('zip')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'zip')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12}>
                {
                  values.contacts.map((contact, index) => (
                    <CreateContactForm
                      index={index}
                      contact={contact}
                      formikProps={formikProps}
                      // This rule is generally good but there is no other good key to use here
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    />
                  ))
                }
              </Col>
              <Col xs={12} className="mb-3">
                <small className="text-muted">
                  Your breeder name, city, state and zip code will
                  be visible on your seller profile.
                  Your street address will not be publicly visible.
                </small>
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="bg-white">
                {footer(!!values?.contacts[0]?.contact_preference?.length && isValid, false)}
              </Col>
            </Row>
          </form>
        );
      }}
    </Formik>
  );
};

export default SellerDetailsStep;
